import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  JOB_CADENCE_LABELS,
  periodLabel,
  studentFullName,
  studentName,
  studentSortName,
  TERMS,
  TERM_LABELS,
  type Assessment,
  type Student,
  type Term,
} from '@td/shared';
import { useSchoolYears } from '../hooks/useSchoolYear';
import { useYearStudents } from '../hooks/useStudents';
import { useAssessments, useSaveScores, useScores } from '../hooks/useAssessments';
import { useStudentFlags } from '../hooks/useStudentFlags';
import { useArchivedJobYear } from '../hooks/useJobs';
import { StudentAvatar } from '../components/StudentAvatar';
import { StudentProfile } from '../components/StudentProfile';
import { FlagBadge } from '../components/FlagBadge';
import { ScoreGrid } from '../components/ScoreGrid';
import { GrowthView } from '../components/GrowthView';
import { ClassCharts } from '../components/ClassCharts';
import { usePrivacy } from '../privacy/PrivacyContext';

/**
 * Past years, kept to be looked at.
 *
 * Read-only throughout, and reached from Settings rather than the sidebar: a
 * teacher walks past the sidebar forty times a day and this is somewhere she
 * goes twice a year — when a parent asks about a sibling, or when someone
 * wants last year's growth. Nothing on this page writes, so a nostalgic
 * afternoon in here cannot cost her anything.
 */
export function ArchivesPage() {
  const { data: years = [] } = useSchoolYears();
  const [year, setYear] = useState<string | null>(null);
  const { privacy, toggle } = usePrivacy();

  // Default to the year most recently put away.
  useEffect(() => {
    if (!year && years.length > 0) setYear(years[0]!.label);
  }, [years, year]);

  const chosen = years.find((y) => y.label === year) ?? null;

  if (years.length === 0) {
    return (
      <div className="mx-auto max-w-2xl">
        <Header />
        <p className="mt-4 text-sm leading-relaxed text-walnut">
          Nothing is archived yet. At the end of the school year — the last day you set under
          Settings → School year — this class, their seating and all of their data are put away
          here, and the new year starts with an empty roster.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      <Header>
        <button onClick={toggle} aria-pressed={privacy} className="btn-secondary">
          {privacy ? 'Privacy on' : 'Privacy'}
        </button>
        <button onClick={() => window.print()} className="btn-secondary">
          Print
        </button>
      </Header>

      <div
        className="mb-5 flex flex-wrap gap-1.5 print:hidden"
        role="group"
        aria-label="School year"
      >
        {years.map((y) => (
          <button
            key={y.id}
            onClick={() => setYear(y.label)}
            aria-pressed={y.label === year}
            className={[
              'rounded-lg px-3 py-1.5 text-sm transition-colors',
              y.label === year ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
            ].join(' ')}
          >
            {y.label}
          </button>
        ))}
      </div>

      {chosen && (
        <p className="mb-4 text-xs text-stone">
          {chosen.students} student{chosen.students === 1 ? '' : 's'} · {chosen.assessments}{' '}
          assessment{chosen.assessments === 1 ? '' : 's'} · archived{' '}
          {new Date(chosen.archivedAt).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
          {chosen.manual ? ', by hand' : ''}
        </p>
      )}

      {year && <YearView key={year} year={year} />}
    </div>
  );
}

function Header({ children }: { children?: React.ReactNode }) {
  return (
    <header className="page-header flex flex-wrap items-end justify-between gap-3 print:hidden">
      <div className="min-w-0">
        <h1 className="text-2xl">Archives</h1>
        <p className="mt-0.5 text-sm text-stone">
          Past classes and their data, exactly as they were left. Nothing here can be changed.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {children}
        <Link to="/settings" className="btn-soft">
          Back to Settings
        </Link>
      </div>
    </header>
  );
}

/* ------------------------------------------------------------ one year */

const TABS = ['class', 'data', 'jobs'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = {
  class: 'The class',
  data: 'Their data',
  jobs: 'Their jobs',
};

function YearView({ year }: { year: string }) {
  const [tab, setTab] = useState<Tab>('class');

  return (
    <>
      <div className="mb-4 flex gap-1 print:hidden" role="group" aria-label="View">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className={[
              'rounded-lg px-2.5 py-1 text-xs transition-colors',
              tab === t ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
            ].join(' ')}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === 'class' && <ArchivedClass year={year} />}
      {tab === 'data' && <ArchivedData year={year} />}
      {tab === 'jobs' && <ArchivedJobs year={year} />}
    </>
  );
}

/* ----------------------------------------------------------- the jobs */

/**
 * A year of the job board, as a record rather than a board.
 *
 * Paging through forty weekly turns one at a time to find out who was line
 * leader in October would be a worse way to read a finished year than simply
 * listing it. The counts come first, because "how many turns did each child
 * get" is the only question anybody is likely to bring to this page — and it
 * is the question the board itself exists to answer.
 */
function ArchivedJobs({ year }: { year: string }) {
  const { data, isLoading } = useArchivedJobYear(year);
  const { data: students = [] } = useYearStudents(year);

  const byId = useMemo(() => new Map(students.map((s) => [s.id, s])), [students]);
  const name = (id: string) => {
    const student = byId.get(id);
    return student ? studentName(student) : 'A child who has since been removed';
  };

  if (isLoading) return <p className="text-sm text-stone">Loading…</p>;

  if (!data || data.jobs.length === 0) {
    return (
      <p className="card max-w-lg text-sm text-stone">
        No jobs were given out in {year}.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <section className="card">
        <h2 className="mb-2 text-base">How many turns each child had</h2>

        <ul className="flex flex-wrap gap-1.5">
          {data.totals.map((row) => (
            <li
              key={row.studentId}
              className="rounded-full border border-sand bg-white px-2.5 py-1 text-xs text-espresso"
            >
              {name(row.studentId)} · {row.count}
            </li>
          ))}
        </ul>
      </section>

      {data.jobs.map((job) => (
        <section key={job.id} className="card">
          <h2 className="mb-2 flex items-baseline gap-2 text-base">
            {job.name}
            <span className="text-xs text-stone">
              {JOB_CADENCE_LABELS[job.cadence].toLowerCase()} · {job.turns.length} turns
            </span>
          </h2>

          <ol className="space-y-1">
            {job.turns.map((turn) => (
              <li
                key={turn.period}
                className="flex items-baseline justify-between gap-3 border-b border-sand py-1 text-sm last:border-0"
              >
                <span className="shrink-0 text-xs text-stone">
                  {periodLabel(job.cadence, turn.period)}
                </span>
                <span className="min-w-0 text-right text-espresso">
                  {turn.studentIds.map(name).join(', ')}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------- the class */

function ArchivedClass({ year }: { year: string }) {
  const { data: students = [], isLoading } = useYearStudents(year);
  const { data: flagDefs = [] } = useStudentFlags();
  const { privacy } = usePrivacy();
  const [open, setOpen] = useState<Student | null>(null);

  const sorted = useMemo(
    () => [...students].sort((a, b) => studentSortName(a).localeCompare(studentSortName(b))),
    [students],
  );

  // The panel holds a copy of the student it was opened with; refetching in
  // the background should not leave a stale one on screen.
  const showing = open ? (students.find((s) => s.id === open.id) ?? open) : null;

  if (isLoading) return <p className="text-sm text-stone">Loading…</p>;

  if (sorted.length === 0) {
    return <p className="text-sm text-stone">No roster was recorded for {year}.</p>;
  }

  return (
    <>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {sorted.map((s) => (
          <li key={s.id}>
            <button
              onClick={() => setOpen(s)}
              className="flex w-full items-center gap-3 rounded-card border border-sand bg-white p-3 text-left transition-colors hover:border-clay hover:bg-linen/40"
            >
              <StudentAvatar student={s} size={40} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-espresso">
                  {privacy ? `${studentSortName(s).slice(0, 1)}.` : studentFullName(s)}
                </span>
                <span className="mt-0.5 flex flex-wrap gap-1">
                  {s.flags.map((f) => (
                    <FlagBadge key={f} flagKey={f} defs={flagDefs} />
                  ))}
                  {s.notes.length > 0 && (
                    <span className="text-[11px] text-stone">
                      {s.notes.length} note{s.notes.length === 1 ? '' : 's'}
                    </span>
                  )}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      {showing && (
        <StudentProfile
          student={showing}
          readOnly
          onEdit={() => undefined}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/* ----------------------------------------------------------- the data */

const VIEWS = ['scores', 'growth', 'charts'] as const;
type DataView = (typeof VIEWS)[number];
const VIEW_LABELS: Record<DataView, string> = {
  scores: 'Scores',
  growth: 'Growth',
  charts: 'Class charts',
};

function ArchivedData({ year }: { year: string }) {
  const { data: assessments = [], isLoading } = useAssessments(year);
  const { data: students = [] } = useYearStudents(year);

  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [view, setView] = useState<DataView>('scores');
  const [term, setTerm] = useState<Term>('EOY');
  const [measureId, setMeasureId] = useState<string | null>(null);
  const [date, setDate] = useState('');

  const assessment: Assessment | null = assessments.find((a) => a.id === assessmentId) ?? null;
  const { data: scores = [] } = useScores(assessment?.id ?? null);

  /*
   * The grid needs a mutation object, and this one is simply never fired: it
   * is only reachable from a cell, and every cell here is read-only. Passing
   * the real hook keeps the component's contract honest rather than faking a
   * shape that would drift the first time the hook changes.
   */
  const save = useSaveScores(assessment?.id ?? '');

  /** The days scores were actually entered on, for assessments kept by date. */
  const dates = useMemo(
    () => [...new Set(scores.map((s) => s.date).filter(Boolean))].sort().reverse(),
    [scores],
  );

  // Open the first assessment, and keep the measure pointing at a real one.
  useEffect(() => {
    if (!assessmentId && assessments.length > 0) setAssessmentId(assessments[0]!.id);
  }, [assessments, assessmentId]);

  // Land on the most recent day recorded rather than an empty grid.
  useEffect(() => {
    if (dates.length > 0 && !dates.includes(date)) setDate(dates[0]!);
  }, [dates, date]);

  useEffect(() => {
    if (assessment && !assessment.measures.some((m) => m.id === measureId)) {
      setMeasureId(assessment.measures[0]?.id ?? null);
    }
  }, [assessment, measureId]);

  const measure = assessment?.measures.find((m) => m.id === measureId) ?? null;

  if (isLoading) return <p className="text-sm text-stone">Loading…</p>;

  if (assessments.length === 0) {
    return <p className="text-sm text-stone">No assessment data was recorded in {year}.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <select
          className="field w-auto"
          value={assessmentId ?? ''}
          onChange={(e) => setAssessmentId(e.target.value)}
          aria-label="Assessment"
        >
          {assessments.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.subject ? ` — ${a.subject}` : ''}
            </option>
          ))}
        </select>

        {assessment && assessment.measures.length > 1 && view !== 'scores' && (
          <select
            className="field w-auto"
            value={measureId ?? ''}
            onChange={(e) => setMeasureId(e.target.value)}
            aria-label="Measure"
          >
            {assessment.measures.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        )}

        {assessment && !assessment.termly && view === 'scores' && dates.length > 0 && (
          <select
            className="field w-auto"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Day"
          >
            {dates.map((d) => (
              <option key={d} value={d}>
                {new Date(`${d}T12:00:00`).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </option>
            ))}
          </select>
        )}

        {assessment?.termly && view !== 'growth' && (
          <div className="flex gap-1" role="group" aria-label="Term">
            {TERMS.map((t) => (
              <button
                key={t}
                onClick={() => setTerm(t)}
                aria-pressed={term === t}
                className={[
                  'rounded-lg px-2.5 py-1 text-xs transition-colors',
                  term === t ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
                ].join(' ')}
              >
                {TERM_LABELS[t]}
              </button>
            ))}
          </div>
        )}

        <div className="ml-auto flex gap-1" role="group" aria-label="View">
          {VIEWS.map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              aria-pressed={view === v}
              className={[
                'rounded-lg px-2.5 py-1 text-xs transition-colors',
                view === v ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
              ].join(' ')}
            >
              {VIEW_LABELS[v]}
            </button>
          ))}
        </div>
      </div>

      {assessment && view === 'scores' && (
        <ScoreGrid
          assessment={assessment}
          students={students}
          scores={scores}
          term={assessment.termly ? term : null}
          date={date}
          readOnly
          save={save}
          onSaved={() => undefined}
        />
      )}

      {assessment && measure && view === 'growth' && (
        <GrowthView assessment={assessment} measure={measure} students={students} scores={scores} />
      )}

      {assessment && measure && view === 'charts' && (
        <ClassCharts
          assessment={assessment}
          measure={measure}
          students={students}
          scores={scores}
          term={term}
        />
      )}
    </div>
  );
}
